import {
  Colors,
  createGlobalStyle,
  CSSProperties,
  DefaultTheme
} from 'styled-components'

export const fontSizes = [
  '48px',
  '32px',
  '24px',
  '16px',
  '40px',
  '56px',
  '21px',
  '20px',
  '36px',
  '28px',
  '14px',
  '12px'
]

const letterSpacing = [
  '2.67px',
  '2px',
  '1.75px',
  '1.17px',
  '0.85px',
  '1.5px',
  '1.46px',
  '1.2px',
  '0.5px',
  '0.33px',
  '0.43px'
]

export const lineHeight = [1.125, 1.25, 1.5, 1.67, 1.8, 1.3, 1.2, 1.43, 2]
const breakpoints = ['41.6875em', '48em', '51.25em', '62em', '73.75em', '82em'] // 667px, 768px, 820px, 992px, 1180px, 1312px

export const colors: Colors = {
  black: '#000000',
  border: '#E0E0E0',
  label: '#919191',
  main: '#95358F',
  secondary: '#353535',
  white: '#FFFFFF',
  lightGrey: '#C0C0C0',
  inputBorder: '#DCDCDC',
  textLink: '#1573FF',
  darkMain: '#7B2276',
  tableAlternate: '#F9F3F8',
  focusShadow: 'rgba(149, 53, 143, 0.2)',
  caution: '#DD5151',
  lightBlue: '#EFF3F4',
}

export const fonts = {
  heavy: "'Roboto', Arial, sans-serif",
  regular: "'Roboto', Arial, sans-serif"
}

const buttons = {
  icon: {
    padding: '0'
  },
  primary: {
    background: `${colors.secondary}`,
    borderRadius: '48px',
    color: `${colors.main}`,
    fontSize: '18px',
    padding: '16px 24px'
  },
  secondary: {
    background: `${colors.secondary}`,
    borderRadius: '48px',
    color: `${colors.black}`,
    fontSize: '18px',
    padding: '16px 24px'
  }
}

const headings = {
  h1: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[0],
    letterSpacing: letterSpacing[1],
    lineHeight: lineHeight[0],
    textTransform: 'capitalize'
  } as CSSProperties,
  h1M: {
    color: colors.main,
    fontFamily: fonts.heavy,
    fontSize: fontSizes[6],
    letterSpacing: letterSpacing[5],
    lineHeight: lineHeight[0],
    textTransform: 'capitalize'
  } as CSSProperties,
  h2: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[5],
    lineHeight: lineHeight[0],
    textTransform: 'capitalize'
  } as CSSProperties,
  h2M: {
    font: `${fontSizes[2]} / ${lineHeight[5]} ${fonts.heavy}`,
    letterSpacing: letterSpacing[2],
    textTransform: 'capitalize'
  } as CSSProperties,
  h3: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[8],
    letterSpacing: letterSpacing[1],
    lineHeight: lineHeight[6],
    textTransform: 'capitalize'
  } as CSSProperties,
  h3M: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[7],
    letterSpacing: letterSpacing[6],
    lineHeight: lineHeight[6],
    textTransform: 'capitalize'
  } as CSSProperties,
  h4: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[1],
    letterSpacing: letterSpacing[0],
    lineHeight: lineHeight[1],
    textTransform: 'capitalize'
  } as CSSProperties,
  h4M: {
    font: `${fontSizes[3]} / ${lineHeight[1]} ${fonts.heavy}`,
    letterSpacing: letterSpacing[3],
    textTransform: 'capitalize'
  } as CSSProperties,
  h4T: {
    font: `${fontSizes[9]} / ${lineHeight[5]} ${fonts.heavy}`,
    letterSpacing: letterSpacing[1],
    textTransform: 'capitalize'
  } as CSSProperties,
  h5: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[2],
    letterSpacing: letterSpacing[2],
    lineHeight: lineHeight[2],
    textTransform: 'capitalize'
  } as CSSProperties,
  h5M: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[10],
    letterSpacing: letterSpacing[7],
    lineHeight: lineHeight[2],
    textTransform: 'capitalize'
  } as CSSProperties,
  h5T: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[7],
    letterSpacing: letterSpacing[6],
    lineHeight: lineHeight[4],
    textTransform: 'capitalize'
  } as CSSProperties,
  h6: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[3],
    letterSpacing: letterSpacing[3],
    lineHeight: lineHeight[2],
    textTransform: 'capitalize'
  } as CSSProperties,
  h6M: {
    fontFamily: fonts.heavy,
    fontSize: fontSizes[11],
    letterSpacing: letterSpacing[10],
    lineHeight: lineHeight[3],
    textTransform: 'capitalize'
  } as CSSProperties
}

const paragraphs = {
  body: {
    font: `${fontSizes[2]} / ${lineHeight[2]} ${fonts.regular}`,
    letterSpacing: letterSpacing[8]
  },
  bodyT: {
    font: `${fontSizes[2]} / ${lineHeight[2]} ${fonts.regular}`,
    letterSpacing: letterSpacing[8]
  },
  bodyM: {
    font: `${fontSizes[10]} / ${lineHeight[7]} ${fonts.regular}`,
    letterSpacing: letterSpacing[3]
  },
  caption: {
    font: `${fontSizes[3]} / ${lineHeight[2]} ${fonts.regular}`,
    letterSpacing: letterSpacing[9]
  }
}

const scoreboardTheme: DefaultTheme = {
  borderRadius: '5px',
  breakpoints,
  buttons,
  colors,
  fontSize: fontSizes,
  fonts,
  headings,
  letterSpacing,
  lineHeight,
  mediaQueries: {
    extraLarge: `@media screen and (min-width: ${breakpoints[3]})`,
    medium: `@media screen and (min-width: ${breakpoints[1]})`,
    mediumOnly: `@media screen and (min-width: ${breakpoints[1]}) and (max-width: 1023px)`,
    mediumDown: `@media screen and (max-width: ${breakpoints[1]})`,
    large: `@media screen and (min-width: ${breakpoints[2]})`,
    largeDown: `@media screen and (max-width: ${breakpoints[2]})`,
    largeOnly: `@media screen and (min-width: ${breakpoints[2]}) and (max-width: 1279px)`,
    small: `@media screen and (min-width: ${breakpoints[0]})`,
    smallOnly: `@media screen and (max-width: ${breakpoints[0]})`
  },
  paragraphs
}

const GlobalStyle = createGlobalStyle`

  html {
    height: 100%;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
    font-size: 16px;
    color: ${scoreboardTheme.colors.secondary};
  }

  body {
    width: 100%;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;
  }

  p {
    font-size: 16px;
    line-height: 21px;
  }

  a {
    text-decoration: none;
    cursor: pointer;
  }
`

export { scoreboardTheme, GlobalStyle, breakpoints }
